// ---------------------------------------------------------------------------
// Estratto VERBATIM da CRMMaster.tsx (Task #3873 — split meccanico).
// Nessuna modifica logica: solo header import e keyword export.
// ---------------------------------------------------------------------------
import React, { useState, useEffect, useCallback } from 'react';
import { useToast } from '@/hooks/use-toast';
import { toErr } from '@easyflow/shared';
import {
  FileText, FileSignature, CreditCard, Search, Plus, Trash2, Send, Eye, Clock, DollarSign, AlertTriangle, CheckCircle, XCircle, RefreshCw, Calendar, Download
} from 'lucide-react';
import api from '@/lib/api';
import { exportToCSV } from '@/components/crm/csv-export';
import { CRM_BASE } from './types';
// Task #3875 — DTO risposta condivisi (alias per minimizzare il churn).
import type {
  CrmMasterQuote as Quote,
  CrmMasterContract as Contract,
  CrmMasterContractsDashboard as DashboardData,
  CrmMasterContractSignature as ContractSignature,
  CrmMasterContract as ContractPreview,
  CrmMasterContractTemplate as ContractTemplate,
  CrmMasterPaymentPlan as PaymentPlan,
  CrmMasterPaymentPlan as PaymentPlanDetail,
  CrmMasterQuotePaymentInfo as QuotePaymentInfo,
} from '@easyflow/shared';
import {
  isUserTypingInForm, Tip, contractStatusBadge, contractPipelineStages,
  Badge, AdvFilters, EMPTY_ADV_FILTERS, countAdvFilters, FilteredBadge,
  advFiltersToParams, AdvancedFilters
} from './shared';
import { ContractsKanban } from './contratti/ContractsKanban';
import { PaymentPlansSection } from './contratti/PaymentPlansSection';
import { PpDetailModal } from './contratti/PpDetailModal';
import { CreateContractModal } from './contratti/CreateContractModal';
import { SignaturesModal } from './contratti/SignaturesModal';
import { ContractPreviewModal } from './contratti/ContractPreviewModal';

export function TabContratti() {
  const { toast } = useToast();
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [acceptedQuotes, setAcceptedQuotes] = useState<Quote[]>([]);
  const [selectedQuoteId, setSelectedQuoteId] = useState('');
  const [signatures, setSignatures] = useState<ContractSignature[]>([]);
  const [showSignatures, setShowSignatures] = useState(false);
  const [saving, setSaving] = useState(false);
  const [contractPreview, setContractPreview] = useState<ContractPreview | null>(null);
  const [templates, setTemplates] = useState<ContractTemplate[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [voipNumbers, setVoipNumbers] = useState<Array<{ type: 'new' | 'portability'; number: string; operator_code?: string; operator_other?: string; codice_migrazione?: string; note?: string }>>([]);
  const [voipOperators, setVoipOperators] = useState<Array<{ code: string; label: string }>>([]);
  const [voipWantsNumbers, setVoipWantsNumbers] = useState<'unset' | 'yes' | 'no'>('unset');
  const [templatePreview, setTemplatePreview] = useState('');
  const [installmentPlan, setInstallmentPlan] = useState<Array<{ number: number; amount: number; due_date: string; description: string }>>([]);
  const [selectedQuoteData, setSelectedQuoteData] = useState<Quote | null>(null);
  const [planError, setPlanError] = useState('');
  const [dateAdjustPending, setDateAdjustPending] = useState<{ index: number; oldDate: string; newDate: string; deltaDays: number } | null>(null);
  const [durationMonths, setDurationMonths] = useState<number>(12);
  const [installmentFrequency, setInstallmentFrequency] = useState<'monthly' | 'quarterly' | 'semiannual' | 'annual'>('monthly');
  const [startDate, setStartDate] = useState<string>(new Date().toISOString().split('T')[0] ?? '');
  const [dashboard, setDashboard] = useState<DashboardData | null>(null);
  const [pipelineContracts, setPipelineContracts] = useState<Contract[]>([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchFilter, setSearchFilter] = useState('');
  const [advFilters, setAdvFilters] = useState<AdvFilters>(EMPTY_ADV_FILTERS);
  const [viewMode, setViewMode] = useState<'table' | 'kanban'>('table');
  const [paymentPlans, setPaymentPlans] = useState<PaymentPlan[]>([]);
  const [showPaymentPlans, setShowPaymentPlans] = useState(true);
  const [ppStatusFilter, setPpStatusFilter] = useState('');
  // Task #3090 — selezione contratto per filtrare i Piani Rateali (quote_id + label).
  const [selectedContract, setSelectedContract] = useState<{ quoteId: string; label: string } | null>(null);
  const [ppDetail, setPpDetail] = useState<PaymentPlanDetail | null>(null);
  const [quotePaymentInfo, setQuotePaymentInfo] = useState<QuotePaymentInfo | null>(null);
  const [loadingPaymentInfo, setLoadingPaymentInfo] = useState(false);
  // Task #1275 — Contratto Addizionale
  const [parentContracts, setParentContracts] = useState<Contract[]>([]);
  const [parentContractId, setParentContractId] = useState<string>('');
  const [addonServices, setAddonServices] = useState<{ setup: boolean; formazione: boolean; assistenza: boolean }>({ setup: false, formazione: false, assistenza: false });
  const [addonPaymentDays, setAddonPaymentDays] = useState<number>(15);
  const [addonSchedulingDays, setAddonSchedulingDays] = useState<number>(10);
  const [addonTitle, setAddonTitle] = useState<string>('');
  const [addonSubtitle, setAddonSubtitle] = useState<string>('');

  const loadContracts = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const params = advFiltersToParams(advFilters, { from: 'date_from', to: 'date_to' });
      if (statusFilter) params.set('status', statusFilter);
      const qs = params.toString();
      const dashQs = advFiltersToParams(advFilters, { from: 'date_from', to: 'date_to' }).toString();
      const results = await Promise.allSettled([
        api.get<Contract[] | { contracts?: Contract[] }>(`${CRM_BASE}/contracts${qs ? `?${qs}` : ''}`),
        api.get<DashboardData>(`${CRM_BASE}/contracts/dashboard${dashQs ? `?${dashQs}` : ''}`),
        api.get<Contract[]>(`${CRM_BASE}/contracts/pipeline`),
        api.get<PaymentPlan[]>(`${CRM_BASE}/payment-plans`),
      ]);
      const [cRes, dRes, pRes, ppRes] = results;
      if (cRes.status === 'fulfilled' && cRes.value?.success && cRes.value.data) setContracts(Array.isArray(cRes.value.data) ? cRes.value.data : (cRes.value.data).contracts || []);
      if (dRes.status === 'fulfilled' && dRes.value?.success && dRes.value.data) setDashboard(dRes.value.data);
      if (pRes.status === 'fulfilled' && pRes.value?.success && pRes.value.data) setPipelineContracts(Array.isArray(pRes.value.data) ? pRes.value.data : []);
      if (ppRes.status === 'fulfilled' && ppRes.value?.success && ppRes.value.data) setPaymentPlans(Array.isArray(ppRes.value.data) ? ppRes.value.data : []);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [statusFilter, advFilters]);

  useEffect(() => { void loadContracts(); }, [loadContracts]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (isUserTypingInForm()) return;
      void loadContracts(true);
    }, 30000);
    return () => clearInterval(interval);
  }, [loadContracts]);

  const downloadContractPdf = async (contractId: string) => {
    try {
      const token = localStorage.getItem('edgvoip_master_token') || localStorage.getItem('master_token') || localStorage.getItem('token');
      const response = await fetch(`/api${CRM_BASE}/contracts/${contractId}/pdf`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Errore scaricamento PDF');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Contratto_${contractId}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast({ title: "PDF scaricato", description: "Il contratto è stato scaricato." });
    } catch (err: unknown) {
      const __err = toErr(err);
      toast({ title: "Errore download", description: __err?.message || 'Errore sconosciuto', variant: 'destructive' });
    }
  };

  const openCreate = async () => {
    const [quotesRes, templatesRes] = await Promise.all([
      api.get(`${CRM_BASE}/quotes`),
      api.get<ContractTemplate[]>(`${CRM_BASE}/contract-templates`),
    ]);
    if (quotesRes.success && quotesRes.data) {
      const all: Quote[] = Array.isArray(quotesRes.data) ? quotesRes.data : (quotesRes.data as { quotes?: Quote[] }).quotes || [];
      setAcceptedQuotes(all.filter((q) => ['accepted', 'payment_setup_sent', 'payment_confirmed'].includes(q.status)));
    }
    if (templatesRes.success && templatesRes.data) {
      setTemplates(Array.isArray(templatesRes.data) ? templatesRes.data : []);
      const def = (Array.isArray(templatesRes.data) ? templatesRes.data : []).find((t) => t.is_default);
      if (def) setSelectedTemplateId(def.id);
    }
    try {
      const opsRes = await api.get(`${CRM_BASE}/telco-operators`);
      if (opsRes.success && Array.isArray(opsRes.data)) setVoipOperators(opsRes.data.map((o) => ({ code: o.code, label: o.label })));
    } catch {}
    setSelectedQuoteId('');
    setSelectedQuoteData(null);
    setInstallmentPlan([]);
    setPlanError('');
    setTemplatePreview('');
    setDurationMonths(12);
    setInstallmentFrequency('monthly');
    setStartDate(new Date().toISOString().split('T')[0] ?? '');
    // Task #1275
    setParentContracts([]);
    setParentContractId('');
    setAddonServices({ setup: false, formazione: false, assistenza: false });
    setAddonPaymentDays(15);
    setAddonSchedulingDays(10);
    setAddonTitle('');
    setAddonSubtitle('');
    setShowCreate(true);
  };

  const generateInstallmentPlan = (total: number, duration: number, frequency: 'monthly' | 'quarterly' | 'semiannual' | 'annual', start: string) => {
    const freqMonths = frequency === 'monthly' ? 1 : frequency === 'quarterly' ? 3 : frequency === 'semiannual' ? 6 : 12;
    const count = Math.max(1, Math.floor(duration / freqMonths));
    const perInst = Math.round((total / count) * 100) / 100;
    const startD = new Date(start);
    return Array.from({ length: count }, (_, i) => {
      const d = new Date(startD);
      d.setMonth(d.getMonth() + freqMonths * (i + 1));
      return { number: i + 1, amount: i === count - 1 ? Math.round((total - perInst * (count - 1)) * 100) / 100 : perInst, due_date: d.toISOString().split('T')[0] ?? '', description: '' };
    });
  };

  const onQuoteSelected = async (quoteId: string) => {
    setSelectedQuoteId(quoteId);
    const q = acceptedQuotes.find((aq) => aq.id === quoteId);
    setSelectedQuoteData(q || null);
    setQuotePaymentInfo(null);
    setParentContractId('');
    // Task #1275 — carica contratti madre candidati per il cliente
    if (q?.pipeline_entry_id) {
      try {
        const r = await api.get(`${CRM_BASE}/pipeline-entries/${q.pipeline_entry_id}/active-contracts`);
        if (r.success && Array.isArray(r.data)) setParentContracts(r.data); else setParentContracts([]);
      } catch { setParentContracts([]); }
    } else {
      setParentContracts([]);
    }
    if (q) {
      const total = Number(q.total_gross ?? 0);
      setLoadingPaymentInfo(true);
      try {
        const payInfo = await api.get<QuotePaymentInfo>(`${CRM_BASE}/quotes/${quoteId}/payment-info`);
        if (payInfo.success && payInfo.data) {
          const pd = payInfo.data;
          setQuotePaymentInfo(pd);
          // Task #3875 — installment_plan è jsonb (unknown sul wire): narrowing esplicito.
          const rawPlan = pd.installment_plan;
          if (pd.has_payment_plan && Array.isArray(rawPlan)) {
            const planRows = rawPlan as Array<{ number?: number; amount?: string | number; due_date?: string; description?: string }>;
            setInstallmentPlan(planRows.map((inst, i: number) => ({
              number: inst.number ?? i + 1,
              amount: parseFloat(String(inst.amount)) || 0,
              due_date: inst.due_date || '',
              description: inst.description || '',
            })));
            if (pd.duration_months) setDurationMonths(pd.duration_months);
            if (pd.start_date) setStartDate(String(pd.start_date));
            const freq = pd.frequency;
            if (freq === 'monthly' || freq === 'quarterly' || freq === 'semiannual' || freq === 'annual') setInstallmentFrequency(freq);
          } else {
            setInstallmentPlan(generateInstallmentPlan(total, durationMonths, installmentFrequency, startDate));
          }
        } else {
          setInstallmentPlan(generateInstallmentPlan(total, durationMonths, installmentFrequency, startDate));
        }
      } catch {
        setInstallmentPlan(generateInstallmentPlan(total, durationMonths, installmentFrequency, startDate));
      } finally {
        setLoadingPaymentInfo(false);
      }
      setPlanError('');
    }
  };

  const onDurationChange = (months: number) => {
    setDurationMonths(months);
    if (selectedQuoteData) {
      const total = Number(selectedQuoteData.total_gross ?? 0);
      setInstallmentPlan(generateInstallmentPlan(total, months, installmentFrequency, startDate));
    }
  };

  const onFrequencyChange = (freq: 'monthly' | 'quarterly' | 'semiannual' | 'annual') => {
    setInstallmentFrequency(freq);
    if (selectedQuoteData) {
      const total = Number(selectedQuoteData.total_gross ?? 0);
      setInstallmentPlan(generateInstallmentPlan(total, durationMonths, freq, startDate));
    }
  };

  const onStartDateChange = (date: string) => {
    setStartDate(date);
    if (selectedQuoteData) {
      const total = Number(selectedQuoteData.total_gross ?? 0);
      setInstallmentPlan(generateInstallmentPlan(total, durationMonths, installmentFrequency, date));
    }
  };

  const updateInstallment = (index: number, field: string, value: string | number) => {
    const EMPTY_INST = { number: 0, amount: 0, due_date: '', description: '' };
    if (field === 'due_date' && index < installmentPlan.length - 1) {
      const oldDate = installmentPlan[index]?.due_date;
      const newDate = value;
      if (oldDate && newDate && oldDate !== newDate) {
        const delta = Math.round((new Date(newDate).getTime() - new Date(oldDate).getTime()) / (1000 * 60 * 60 * 24));
        if (delta !== 0) {
          setInstallmentPlan(prev => {
            const updated = [...prev];
            updated[index] = { ...(updated[index] ?? EMPTY_INST), due_date: String(newDate) };
            return updated;
          });
          setDateAdjustPending({ index, oldDate, newDate: String(newDate), deltaDays: delta });
          return;
        }
      }
    }
    setInstallmentPlan(prev => {
      const updated = [...prev];
      if (field === 'amount') {
        const newAmount = parseFloat(String(value)) || 0;
        updated[index] = { ...(updated[index] ?? EMPTY_INST), amount: newAmount };
        const total = Number(selectedQuoteData?.total_gross ?? 0);
        const othersCount = updated.length - 1;
        if (othersCount > 0) {
          const remaining = Math.round((total - newAmount) * 100) / 100;
          const perOther = Math.round((remaining / othersCount) * 100) / 100;
          let distributed = 0;
          updated.forEach((inst, i) => {
            if (i !== index) {
              if (i === updated.length - 1 || (i === updated.length - 2 && index === updated.length - 1)) {
                inst.amount = Math.round((remaining - distributed) * 100) / 100;
              } else {
                inst.amount = perOther;
                distributed += perOther;
              }
            }
          });
        }
      } else {
        updated[index] = { ...(updated[index] ?? EMPTY_INST), [field]: value };
      }
      return updated;
    });
  };

  const applyDateAdjustment = (adjustSubsequent: boolean) => {
    if (!dateAdjustPending) return;
    const { index, deltaDays } = dateAdjustPending;
    const EMPTY_INST = { number: 0, amount: 0, due_date: '', description: '' };
    if (adjustSubsequent) {
      setInstallmentPlan(prev => {
        const updated = [...prev];
        for (let i = index + 1; i < updated.length; i++) {
          const cur = updated[i] ?? EMPTY_INST;
          const d = new Date(cur.due_date);
          d.setDate(d.getDate() + deltaDays);
          updated[i] = { ...cur, due_date: d.toISOString().split('T')[0] ?? '' };
        }
        return updated;
      });
    }
    setDateAdjustPending(null);
  };

  const addInstallment = () => {
    setInstallmentPlan(prev => {
      const total = Number(selectedQuoteData?.total_gross ?? 0);
      const newCount = prev.length + 1;
      const perInst = Math.round((total / newCount) * 100) / 100;
      const lastDate = prev.length > 0 ? new Date(prev[prev.length - 1]?.due_date ?? startDate) : new Date(startDate);
      const freqMonths = installmentFrequency === 'monthly' ? 1 : installmentFrequency === 'quarterly' ? 3 : installmentFrequency === 'semiannual' ? 6 : 12;
      lastDate.setMonth(lastDate.getMonth() + freqMonths);
      const newPlan = [...prev, { number: newCount, amount: 0, due_date: lastDate.toISOString().split('T')[0] ?? '', description: '' }];
      let distributed = 0;
      return newPlan.map((inst, i) => {
        const amt = i === newCount - 1 ? Math.round((total - distributed) * 100) / 100 : perInst;
        if (i < newCount - 1) distributed += perInst;
        return { ...inst, number: i + 1, amount: amt };
      });
    });
  };

  const removeInstallment = (index: number) => {
    setInstallmentPlan(prev => {
      const total = Number(selectedQuoteData?.total_gross ?? 0);
      const filtered = prev.filter((_, i) => i !== index);
      if (filtered.length === 0) return filtered;
      const perInst = Math.round((total / filtered.length) * 100) / 100;
      let distributed = 0;
      return filtered.map((inst, i) => {
        const amt = i === filtered.length - 1 ? Math.round((total - distributed) * 100) / 100 : perInst;
        if (i < filtered.length - 1) distributed += perInst;
        return { ...inst, number: i + 1, amount: amt };
      });
    });
  };

  const planTotal = installmentPlan.reduce((s, i) => s + (i.amount || 0), 0);
  const quoteTotal = Number(selectedQuoteData?.total_gross ?? 0);
  const planValid = Math.abs(planTotal - quoteTotal) < 0.02;

  const endDate = (() => {
    const d = new Date(startDate);
    d.setMonth(d.getMonth() + durationMonths);
    return d.toISOString().split('T')[0] ?? '';
  })();

  const selectedTemplateIsAddon = !!templates.find((t) => t.id === selectedTemplateId)?.is_addon;
  const addonServiceList = (Object.keys(addonServices) as Array<keyof typeof addonServices>).filter(k => addonServices[k]);

  const handleCreate = async () => {
    if (!selectedQuoteId) return;
    if (installmentPlan.length > 0 && !planValid) {
      setPlanError(`Il totale rate (€ ${planTotal.toFixed(2)}) non corrisponde al totale preventivo (€ ${quoteTotal.toFixed(2)})`);
      return;
    }
    const selectedTpl = templates.find(t => t.id === selectedTemplateId);
    const wantsVoip = !!selectedTpl?.requires_voip_attachments;
    if (wantsVoip && voipWantsNumbers === 'yes' && voipNumbers.length > 0) {
      const errs: string[] = [];
      voipNumbers.forEach((n, i) => {
        if (!n.number || !/^[+0-9 \-]{4,32}$/.test(n.number.trim())) errs.push(`Numerazione #${i + 1}: numero mancante o formato non valido.`);
        if (n.type === 'portability') {
          if (!n.operator_code) errs.push(`Numerazione #${i + 1}: operatore di provenienza obbligatorio (GNP).`);
          if (n.operator_code === 'OTHER' && !(n.operator_other || '').trim()) errs.push(`Numerazione #${i + 1}: specificare nome operatore (campo "Altro").`);
          const cm = (n.codice_migrazione || '').toUpperCase();
          if (!cm) errs.push(`Numerazione #${i + 1}: codice migrazione obbligatorio (GNP).`);
          else if (!/^[A-Z0-9]{8,32}$/.test(cm)) errs.push(`Numerazione #${i + 1}: codice migrazione non valido (8-32 caratteri alfanumerici).`);
        }
      });
      if (errs.length > 0) { alert('Correggi i seguenti errori:\n\n' + errs.join('\n')); return; }
    }
    // Task #1275 — validazione addon
    if (selectedTemplateIsAddon) {
      if (!parentContractId) {
        toast({ title: 'Contratto madre richiesto', description: 'Seleziona il contratto madre firmato', variant: 'destructive' });
        return;
      }
      if (addonServiceList.length === 0) {
        toast({ title: 'Servizi addizionali richiesti', description: 'Seleziona almeno un tipo di servizio', variant: 'destructive' });
        return;
      }
    }
    setSaving(true);
    try {
      const res = await api.post(`${CRM_BASE}/contracts`, {
        quote_id: selectedQuoteId,
        template_id: selectedTemplateId || undefined,
        installment_plan: installmentPlan.length > 0 ? installmentPlan : undefined,
        start_date: startDate,
        end_date: endDate,
        duration_months: durationMonths,
        voip_numbers: wantsVoip && voipWantsNumbers === 'yes' && voipNumbers.length > 0 ? voipNumbers : undefined,
        parent_contract_id: selectedTemplateIsAddon ? parentContractId : undefined,
        addon_service_types: selectedTemplateIsAddon ? addonServiceList : undefined,
        addon_payment_days: selectedTemplateIsAddon ? addonPaymentDays : undefined,
        addon_scheduling_days: selectedTemplateIsAddon ? addonSchedulingDays : undefined,
        addon_title: selectedTemplateIsAddon && addonTitle.trim() ? addonTitle.trim() : undefined,
        addon_subtitle: selectedTemplateIsAddon && addonSubtitle.trim() ? addonSubtitle.trim() : undefined,
      });
      if (!res.success) {
        const resWithDetails = res as { details?: unknown };
        const details = resWithDetails.details;
        const detailsMsg = Array.isArray(details) ? '\n' + details.map((d) => `• ${d.path || ''} ${d.message || ''}`).join('\n') : '';
        throw new Error((res.error || 'Errore creazione contratto') + detailsMsg);
      }
      setShowCreate(false);
      void loadContracts();
    } catch (e: unknown) {
      const __e = toErr(e);
      alert(__e.message || 'Errore creazione contratto');
    } finally {
      setSaving(false);
    }
  };

  const handleSend = async (id: string) => {
    try {
      const res = await api.post(`${CRM_BASE}/contracts/${id}/send`);
      if (!res.success) throw new Error(res.error || 'Errore invio contratto');
      toast({ title: "Contratto inviato", description: "Il contratto è stato inviato con successo via email al cliente." });
      void loadContracts();
    } catch (e: unknown) {
      const __e = toErr(e);
      toast({ title: "Errore invio", description: __e.message || 'Errore sconosciuto', variant: "destructive" });
    }
  };

  const handleCancel = async (id: string) => {
    if (!confirm('Annullare questo contratto?')) return;
    try {
      const res = await api.put(`${CRM_BASE}/contracts/${id}/cancel`);
      if (!res.success) throw new Error(res.error || 'Errore annullamento');
      toast({ title: "Contratto annullato", description: "Il contratto è stato annullato." });
      void loadContracts();
    } catch (e: unknown) {
      const __e = toErr(e);
      toast({ title: "Errore", description: __e.message || 'Errore sconosciuto', variant: "destructive" });
    }
  };

  const handleDeleteContract = async (id: string) => {
    if (!confirm('Eliminare definitivamente questo contratto? L\'operazione non è reversibile.')) return;
    try {
      const res = await api.delete(`${CRM_BASE}/contracts/${id}`);
      if (!res.success) throw new Error(res.error || 'Errore eliminazione');
      toast({ title: "Contratto eliminato", description: "Il contratto è stato eliminato definitivamente." });
      void loadContracts();
    } catch (e: unknown) {
      const __e = toErr(e);
      toast({ title: "Errore", description: __e.message || 'Errore sconosciuto', variant: "destructive" });
    }
  };

  const viewSignatures = async (id: string) => {
    const res = await api.get<ContractSignature[] | { signatures?: ContractSignature[] }>(`${CRM_BASE}/contracts/${id}/signatures`);
    if (res.success && res.data) setSignatures(Array.isArray(res.data) ? res.data : (res.data).signatures || []);
    setShowSignatures(true);
  };

  const viewPreview = async (id: string) => {
    const res = await api.get<ContractPreview>(`${CRM_BASE}/contracts/${id}`);
    if (res.success && res.data) setContractPreview(res.data);
  };

  const filteredContracts = contracts.filter(c => {
    if (searchFilter && !(c.tenant_name || '').toLowerCase().includes(searchFilter.toLowerCase()) && !(c.contract_number || '').toLowerCase().includes(searchFilter.toLowerCase())) return false;
    return true;
  });

  const handleExportCSV = () => {
    exportToCSV(filteredContracts as unknown as Record<string, unknown>[], 'contratti', [
      { key: 'contract_number', label: 'Numero' },
      { key: 'tenant_name', label: 'Cliente' },
      { key: 'quote_number', label: 'Preventivo' },
      { key: 'status', label: 'Stato' },
      { key: 'contract_pipeline_status', label: 'Pipeline' },
      { key: 'start_date', label: 'Inizio' },
      { key: 'end_date', label: 'Fine' },
      { key: 'signed_at', label: 'Firmato' },
    ]);
  };

  const handlePipelineStatusChange = async (contractId: string, newStatus: string) => {
    try {
      await api.put(`${CRM_BASE}/contracts/${contractId}/pipeline-status`, { status: newStatus });
      void loadContracts();
    } catch {}
  };

  const handleAutoProgress = async () => {
    try {
      await api.post(`${CRM_BASE}/contracts/pipeline/auto-progress`);
      void loadContracts();
    } catch {}
  };

  return (
    <div className="space-y-4">
      {countAdvFilters(advFilters) > 0 && (
        <div className="flex justify-end"><FilteredBadge filters={advFilters} /></div>
      )}
      {dashboard && (
        <div className="grid grid-cols-1 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {[
            { label: 'Totale Contratti', value: dashboard.total_contracts, icon: FileSignature, color: 'text-blue-400', fmt: 'num' },
            { label: 'Valore Totale', value: dashboard.total_value, icon: DollarSign, color: 'text-green-400', fmt: 'eur' },
            { label: 'Valore Attivi', value: dashboard.active_value, icon: CheckCircle, color: 'text-emerald-400', fmt: 'eur' },
            { label: 'In Scadenza', value: dashboard.expiring_count, icon: AlertTriangle, color: 'text-amber-400', fmt: 'num' },
            { label: 'Firmati Mese', value: dashboard.signed_this_month, icon: Calendar, color: 'text-purple-400', fmt: 'num' },
            { label: 'Durata Media', value: dashboard.avg_duration_months, icon: Clock, color: 'text-cyan-400', fmt: 'months' },
          ].map((kpi, i) => (
            <div key={i} className="bg-white dark:bg-gray-800 border border-border dark:border-gray-700 rounded-xl p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-muted-foreground dark:text-muted-foreground/70 text-xs">{kpi.label}</span>
                <kpi.icon className={`h-4 w-4 ${kpi.color}`} />
              </div>
              <p className="text-xl font-bold text-foreground dark:text-white">
                {kpi.fmt === 'eur' ? `€ ${Number(kpi.value).toLocaleString('it-IT', { minimumFractionDigits: 2 })}` : kpi.fmt === 'months' ? `${Number(kpi.value).toFixed(1)} mesi` : kpi.value}
              </p>
            </div>
          ))}
        </div>
      )}

      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground/70" />
            <input value={searchFilter} onChange={(e) => setSearchFilter(e.target.value)} placeholder="Cerca contratto..."
              className="pl-9 bg-muted dark:bg-gray-700 border border-border dark:border-gray-600 text-foreground dark:text-white rounded-lg px-3 py-2 text-sm w-56" />
          </div>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
            className="bg-muted dark:bg-gray-700 border border-border dark:border-gray-600 text-foreground dark:text-white rounded-lg px-3 py-2 text-sm">
            <option value="">Tutti gli stati</option>
            <option value="draft">Bozza</option>
            <option value="sent">Inviato</option>
            <option value="signed">Firmato</option>
            <option value="cancelled">Annullato</option>
          </select>
          <AdvancedFilters filters={advFilters} onChange={setAdvFilters} dateLabel="Data inizio" />
          <div className="flex items-center bg-muted dark:bg-gray-700 rounded-lg border border-border dark:border-gray-600">
            <button onClick={() => setViewMode('table')}
              className={`px-3 py-2 text-sm rounded-l-lg ${viewMode === 'table' ? 'bg-blue-600 text-white' : 'text-muted-foreground dark:text-muted-foreground/70'}`}>
              Tabella
            </button>
            <button onClick={() => setViewMode('kanban')}
              className={`px-3 py-2 text-sm rounded-r-lg ${viewMode === 'kanban' ? 'bg-blue-600 text-white' : 'text-muted-foreground dark:text-muted-foreground/70'}`}>
              Pipeline
            </button>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {viewMode === 'kanban' && (
            <button onClick={handleAutoProgress}
              className="flex items-center gap-1 text-muted-foreground dark:text-muted-foreground/70 hover:text-foreground dark:hover:text-white px-3 py-2 rounded-lg text-sm border border-border dark:border-gray-600">
              <RefreshCw className="h-4 w-4" /> Auto-Progress
            </button>
          )}
          <button onClick={handleExportCSV} className="flex items-center gap-1 text-muted-foreground dark:text-muted-foreground/70 hover:text-foreground dark:hover:text-white px-3 py-2 rounded-lg text-sm border border-border dark:border-gray-600">
            <Download className="h-4 w-4" /> CSV
          </button>
          <button onClick={openCreate}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors">
            <Plus className="h-4 w-4" /> Nuovo Contratto
          </button>
        </div>
      </div>

      {viewMode === 'kanban' && (
        <ContractsKanban pipelineContracts={pipelineContracts} handlePipelineStatusChange={handlePipelineStatusChange} />
      )}

      {viewMode === 'table' && (
        <div className="bg-white dark:bg-gray-800 border border-border dark:border-gray-700 rounded-xl overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border dark:border-gray-700">
                <th className="text-left py-3 px-4 text-muted-foreground dark:text-muted-foreground/70 text-sm font-medium">Numero</th>
                <th className="text-left py-3 px-4 text-muted-foreground dark:text-muted-foreground/70 text-sm font-medium">Cliente</th>
                <th className="text-left py-3 px-4 text-muted-foreground dark:text-muted-foreground/70 text-sm font-medium">Preventivo</th>
                <th className="text-left py-3 px-4 text-muted-foreground dark:text-muted-foreground/70 text-sm font-medium">Stato</th>
                <th className="text-left py-3 px-4 text-muted-foreground dark:text-muted-foreground/70 text-sm font-medium">Pipeline</th>
                <th className="text-left py-3 px-4 text-muted-foreground dark:text-muted-foreground/70 text-sm font-medium">Data Firma</th>
                <th className="text-center py-3 px-4 text-muted-foreground dark:text-muted-foreground/70 text-sm font-medium">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="py-8 text-center text-muted-foreground dark:text-muted-foreground/70">Caricamento...</td></tr>
              ) : filteredContracts.length === 0 ? (
                <tr><td colSpan={7} className="py-8 text-center text-muted-foreground dark:text-muted-foreground/70">Nessun contratto</td></tr>
              ) : filteredContracts.map((c) => {
                const pStage = contractPipelineStages.find(s => s.key === c.contract_pipeline_status);
                const isSelected = selectedContract?.quoteId && c.quote_id === selectedContract.quoteId;
                return (
                  <tr
                    key={c.id}
                    onClick={() => {
                      const qid = c.quote_id;
                      if (!qid) return;
                      const label = `${c.contract_number || ''}${c.tenant_name ? ` · ${c.tenant_name}` : ''}`.trim();
                      setSelectedContract((prev) => (prev?.quoteId === qid ? null : { quoteId: qid, label }));
                      setShowPaymentPlans(true);
                    }}
                    className={`border-b border-border dark:border-gray-700/50 transition-colors cursor-pointer ${isSelected ? 'bg-blue-50 dark:bg-blue-900/20 ring-1 ring-inset ring-blue-400/50' : 'hover:bg-muted dark:hover:bg-gray-700/50'}`}
                  >
                    <td className="py-3 px-4 text-foreground dark:text-white font-mono">
                      {c.contract_number}
                      {c.parent_contract_number && (() => {
                        const slug = (c.addon_meta && (typeof c.addon_meta === 'string' ? (() => { try { return JSON.parse(c.addon_meta); } catch { return null; } })() : c.addon_meta))?.type_slug || 'misto';
                        const colorMap: Record<string, string> = {
                          setup: 'bg-green-100 text-green-700 dark:bg-green-800/40 dark:text-green-300',
                          formazione: 'bg-purple-100 text-purple-700 dark:bg-purple-800/40 dark:text-purple-300',
                          assistenza: 'bg-orange-100 text-orange-700 dark:bg-orange-800/40 dark:text-orange-300',
                          misto: 'bg-blue-100 text-blue-700 dark:bg-blue-800/40 dark:text-blue-300',
                        };
                        const cls = colorMap[slug] || colorMap.misto;
                        const parentRow = contracts.find((cc) => cc.contract_number === c.parent_contract_number);
                        return (
                          <button type="button"
                            onClick={(e) => { e.stopPropagation(); if (parentRow) setContractPreview(parentRow); }}
                            className={`ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold cursor-pointer hover:opacity-80 ${cls}`}
                            title={`Addon di ${c.parent_contract_number} — clicca per aprire il madre`}>
                            ADD · {c.parent_contract_number}
                          </button>
                        );
                      })()}
                    </td>
                    <td className="py-3 px-4 text-muted-foreground dark:text-muted-foreground/60">{c.tenant_name || c.tenant_id}</td>
                    <td className="py-3 px-4 text-muted-foreground dark:text-muted-foreground/60">{c.quote_number || '-'}</td>
                    <td className="py-3 px-4">
                      <Badge className={contractStatusBadge[c.status ?? ''] || ''}>{c.status}</Badge>
                    </td>
                    <td className="py-3 px-4">
                      {pStage ? <Badge className={pStage.badge}>{pStage.icon} {pStage.label}</Badge> : <span className="text-muted-foreground/70 text-sm">-</span>}
                    </td>
                    <td className="py-3 px-4 text-muted-foreground dark:text-muted-foreground/60">{c.signed_at ? new Date(c.signed_at).toLocaleDateString('it-IT') : '-'}</td>
                    <td className="py-3 px-4">
                      <div className="flex items-center justify-center gap-1">
                        {(c.status === 'draft' || c.status === 'sent') && (
                          <Tip label="Invia per firma"><button onClick={() => handleSend(c.id)} className="text-muted-foreground dark:text-muted-foreground/70 hover:text-blue-400 p-1"><Send className="h-4 w-4" /></button></Tip>
                        )}
                        {c.status !== 'cancelled' && c.status !== 'signed' && (
                          <Tip label="Annulla"><button onClick={() => handleCancel(c.id)} className="text-muted-foreground dark:text-muted-foreground/70 hover:text-red-400 p-1"><XCircle className="h-4 w-4" /></button></Tip>
                        )}
                        {(c.status === 'cancelled' || c.status === 'draft') && (
                          <Tip label="Elimina"><button onClick={() => handleDeleteContract(c.id)} className="text-muted-foreground dark:text-muted-foreground/70 hover:text-red-500 p-1"><Trash2 className="h-4 w-4" /></button></Tip>
                        )}
                        {(() => {
                          const pp = paymentPlans.find((p) => p.quote_id === c.quote_id);
                          if (pp) {
                            const pmColor = pp.status === 'accepted' ? 'text-green-400' : pp.status === 'refused' ? 'text-red-400' : pp.status === 'sent' ? 'text-yellow-400' : 'text-muted-foreground/70';
                            const pmTitle = pp.status === 'accepted'
                              ? `Pagamento: ${pp.stripe_pm_brand || ''} ${pp.stripe_pm_last4 ? '••••' + pp.stripe_pm_last4 : 'Configurato'}`
                              : pp.status === 'refused' ? `Rifiutato: ${pp.refused_reason || 'Nessun motivo'}` : 'Piano inviato - In attesa';
                            <Tip label={pmTitle}>return <button onClick={() => setPpDetail(pp)} className={`${pmColor} hover:opacity-80 p-1`}><CreditCard className="h-4 w-4" /></button></Tip>;
                          }
                          return null;
                        })()}
                        <Tip label="Anteprima"><button onClick={() => viewPreview(c.id)} className="text-muted-foreground dark:text-muted-foreground/70 hover:text-purple-400 p-1"><FileText className="h-4 w-4" /></button></Tip>
                        <Tip label="Firme"><button onClick={() => viewSignatures(c.id)} className="text-muted-foreground dark:text-muted-foreground/70 hover:text-foreground dark:hover:text-white p-1"><Eye className="h-4 w-4" /></button></Tip>
                        <Tip label="Scarica PDF"><button onClick={() => downloadContractPdf(c.id)} className="text-muted-foreground dark:text-muted-foreground/70 hover:text-green-400 p-1"><Download className="h-4 w-4" /></button></Tip>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <PaymentPlansSection
        paymentPlans={paymentPlans} showPaymentPlans={showPaymentPlans} setShowPaymentPlans={setShowPaymentPlans}
        ppStatusFilter={ppStatusFilter} setPpStatusFilter={setPpStatusFilter}
        selectedContract={selectedContract} setSelectedContract={setSelectedContract}
        setPpDetail={setPpDetail} loadContracts={loadContracts}
      />

      <PpDetailModal ppDetail={ppDetail} setPpDetail={setPpDetail} />

      <CreateContractModal
        showCreate={showCreate} setShowCreate={setShowCreate}
        selectedQuoteId={selectedQuoteId} onQuoteSelected={onQuoteSelected} acceptedQuotes={acceptedQuotes}
        selectedTemplateId={selectedTemplateId} setSelectedTemplateId={setSelectedTemplateId} templates={templates}
        voipNumbers={voipNumbers} setVoipNumbers={setVoipNumbers} voipOperators={voipOperators}
        voipWantsNumbers={voipWantsNumbers} setVoipWantsNumbers={setVoipWantsNumbers}
        selectedTemplateIsAddon={selectedTemplateIsAddon} parentContractId={parentContractId} setParentContractId={setParentContractId} parentContracts={parentContracts}
        addonServices={addonServices} setAddonServices={setAddonServices}
        addonPaymentDays={addonPaymentDays} setAddonPaymentDays={setAddonPaymentDays}
        addonSchedulingDays={addonSchedulingDays} setAddonSchedulingDays={setAddonSchedulingDays}
        addonTitle={addonTitle} setAddonTitle={setAddonTitle} addonSubtitle={addonSubtitle} setAddonSubtitle={setAddonSubtitle}
        selectedQuoteData={selectedQuoteData} quoteTotal={quoteTotal} loadingPaymentInfo={loadingPaymentInfo} quotePaymentInfo={quotePaymentInfo}
        installmentPlan={installmentPlan} durationMonths={durationMonths} onDurationChange={onDurationChange}
        startDate={startDate} onStartDateChange={onStartDateChange} endDate={endDate}
        installmentFrequency={installmentFrequency} onFrequencyChange={onFrequencyChange}
        addInstallment={addInstallment} updateInstallment={updateInstallment} removeInstallment={removeInstallment}
        planValid={planValid} planTotal={planTotal} planError={planError}
        dateAdjustPending={dateAdjustPending} applyDateAdjustment={applyDateAdjustment}
        saving={saving} handleCreate={handleCreate}
      />

      <SignaturesModal showSignatures={showSignatures} setShowSignatures={setShowSignatures} signatures={signatures} />

      <ContractPreviewModal contractPreview={contractPreview} setContractPreview={setContractPreview} />
    </div>
  );
}

